/* ONE PAGE, AS A PROMPT FOR GHL'S AI BUILDER.
 *
 * Proven by hand on 2026-10-16 (docs/prototypes/ghl-prompt.md): her archetype
 * page, written into a plain-language brief and pasted into GHL's AI builder,
 * came back as the page she asked for — her words, her colours, labelled
 * placeholders where a picture belongs. That builder takes a written prompt,
 * not the JSON brief (utils/siteBrief.js), so this writes the prompt from the
 * same page the JSON comes from. No Brill call: it is built here, for free.
 *
 * WHAT KEEPS THE BUILDER FAITHFUL (the same rule as the JSON brief):
 *   - her words go in EXACTLY, and the prompt says so in capitals;
 *   - an empty section is left out, never sent as a heading to fill;
 *   - wherever a picture belongs and she has none, the builder is told to
 *     leave a labelled PLACEHOLDER, never a stock photo or an invented quote
 *     (her call: "adding an image later when there isn't a spot for it is a
 *     pain so placeholders are better");
 *   - her WORTHI lane steers the page in plain words and is never NAMED, so the
 *     builder cannot echo the word onto her public page (80c15d7).
 */

import { kindDef, pictureOf, sectionValues, wordCount, fieldWords } from "./websiteSectionKinds";
import { limitsFor, styleDef } from "./siteStyles";

const str = (v) => String(v ?? "").trim();

/* What each lane asks of a page, in words a builder acts on. Never the lane's
   name — that is Brill's word, not the reader's. */
const LEAN = {
  wealth: "The reader is weighing a real investment in her income. Be direct about the result and what staying stuck costs, and state any price plainly.",
  opportunity: "The reader wants her next step. Lead with where this takes her and show that it is doable.",
  relationships: "The reader cares about the people in her life. Keep it warm and safe, and specific about what changes between people.",
  time: "The reader wants relief. Make it feel easy and calm, and show what she gets back.",
  health: "This is a private, personal decision. Keep it gentle and safe, with real stories and no pressure.",
  identity: "The reader wants to feel like herself. Lead with who she is becoming and make it personal.",
};

/* Her Me → Brand colours, by the job each does (utils/brand.js BRAND_JOBS). */
const COLOUR_JOBS = [
  ["brandPrimary", "headings"],
  ["brandAccent", "main buttons"],
  ["buttonText", "text on main buttons"],
  ["button2", "second buttons"],
  ["highlight", "one highlighted word per headline"],
  ["accent", "accents, icons and shapes"],
  ["bgDark", "dark sections"],
  ["bgPrimary", "the page background"],
  ["textOnLight", "body text"],
  ["textOnDark", "text on dark sections"],
];

const hex = (v) => (/^#?[0-9a-f]{6}$/i.test(str(v)) ? `#${str(v).replace(/^#/, "").toUpperCase()}` : "");

/** Every field of one section that has words, as "Label: value" lines. */
function sectionLines(section) {
  return sectionValues(section).map((v) => `${v.label}: ${v.value.replace(/\s*\n+\s*/g, " / ")}`);
}

/** Fields that run past the room the page's style gives them. */
export function overRoom(sections = [], styleId) {
  const out = [];
  for (const s of sections || []) {
    const limits = limitsFor(s, styleId);
    for (const v of sectionValues(s)) {
      const f = v.part || v.field;
      const room = fieldWords(f, limits);
      const n = wordCount(v.value);
      if (room && n > room) out.push({ section: kindDef(s.sectionType).label, label: v.label, words: n, room });
    }
  }
  return out;
}

/**
 * @param page      the page record — title, style
 * @param sections  its sections, in order
 * @param skin      brandSkin(...).skin — her colours and fonts
 * @param worthi    readWorthi(...) — { primary, secondary } or null
 * @param identity  { businessName, ownerName, logoUrl, ownerPhotoUrl }
 * @returns { prompt, notes } — notes are what she should know before pasting
 */
export function ghlPagePrompt({ page, sections = [], skin = {}, worthi = null, identity = {} } = {}) {
  const style = styleDef(page?.style);
  const written = (sections || []).filter((s) => sectionValues(s).length);
  const notes = [];
  const banner = written.find((s) => kindDef(s.sectionType).id === "banner");
  const action = str(banner?.content?.btn);

  const lines = [];
  lines.push(`Build a single landing page${str(identity.businessName) ? ` for ${str(identity.businessName)}` : ""}: "${str(page?.title) || "Untitled page"}".`
    + (action ? ` The page has one job: get the reader to press "${action}".` : ""));
  lines.push("");

  if (style) {
    lines.push(`Look and feel: ${style.words.join(", ")}. ${style.look[0].toUpperCase()}${style.look.slice(1)}; ${style.corners}. Make it look polished and premium, and work well on a phone.`);
  } else {
    lines.push("Make it look polished and premium, and work well on a phone.");
    notes.push("No style picked for this page, so the builder chooses the look. Pick one in the page's settings.");
  }

  const colours = COLOUR_JOBS.map(([k, job]) => (hex(skin[k]) ? `${hex(skin[k])} for ${job}` : "")).filter(Boolean);
  if (colours.length) lines.push(`Colours (use these exact hex codes): ${colours.join("; ")}.`);
  else notes.push("No brand colours yet, so the builder picks them. Set them in Me → Brand.");
  const fonts = [str(skin.fontHeading) && `${str(skin.fontHeading)} for headlines`, str(skin.fontBody) && `${str(skin.fontBody)} for text`].filter(Boolean);
  if (fonts.length) lines.push(`Fonts: ${fonts.join(", ")}.`);

  const lean = [worthi?.primary && LEAN[worthi.primary.id], worthi?.secondary && LEAN[worthi.secondary.id]].filter(Boolean);
  if (lean.length) lines.push(`Who it is for: ${lean.join(" Also: ")}`);
  lines.push("");

  lines.push("USE MY WORDS EXACTLY AS WRITTEN BELOW. Do not add, rewrite or invent any copy, testimonials, quotes, numbers, client names or claims. Where a section would carry a picture and I have not given one, leave a clearly labelled PLACEHOLDER picture spot for me to fill later, never a stock photo.");
  lines.push(str(identity.logoUrl)
    ? `My logo, top left: ${str(identity.logoUrl)}`
    : "Top left: a PLACEHOLDER logo spot labelled \"Logo\" (an image spot, not text).");
  if (str(identity.ownerPhotoUrl)) lines.push(`My photo, for the banner or the About section: ${str(identity.ownerPhotoUrl)}`);
  lines.push("");

  written.forEach((s, i) => {
    const kind = kindDef(s.sectionType);
    lines.push(`SECTION ${i + 1} — ${kind.label}`);
    for (const l of sectionLines(s)) lines.push(l);
    /* Her picture for this section, by link — a pasted link is what the
       builder downloaded on 2026-10-16, so it is the form that works. */
    if (kind.picture && /^https?:\/\/\S+$/i.test(pictureOf(s.content))) lines.push(`Picture for this section (use this exact image): ${pictureOf(s.content)}`);
    lines.push("");
  });

  const skipped = (sections || []).length - written.length;
  if (skipped > 0) notes.push(`${skipped} empty section${skipped === 1 ? " is" : "s are"} left out, so the builder has nothing to invent.`);
  if (!written.length) notes.push("Nothing on this page is written yet.");
  for (const o of overRoom(written, page?.style)) {
    notes.push(`${o.section} · ${o.label}: ${o.words} words, room for about ${o.room}. It will run long on the page.`);
  }

  return { prompt: lines.join("\n").replace(/\n{3,}/g, "\n\n").trim(), notes };
}
